import React, { useState } from 'react';
import { connect } from 'react-redux';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { UploadMethodsWindow } from '@ff-os/shared-react/components/upload-methods/UploadMethodsWindow';
import { selectFailedUploadsCount, selectSuccessfulUploadsCount } from '@ff-scene-plugins/photo-prints-redux/slices/groupsUiSlice';
import { selectPhotoIdsByGroupId, selectPhotosByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import { selectIsEmptyGroup } from '../../../_redux-groups';
import { usePhotoPrintsUploadMethods } from '../../../createPhotoPrintsUploadMethods';
import { uploadImages } from '../../../thunks/uploadImages';
import { incrementWithScheduledReset, updateWithScheduledReset } from '../../../thunks/withScheduledReset';
import { EmptyPhotoPrintsGroup } from './EmptyPhotoPrintsGroup';
import { NotEmptyPhotoPrintsGroup } from './NotEmptyPhotoPrintsGroup';
import { PhotoPrintGroupPanel } from './PhotoPrintGroupPanel';
import { PhotoPrintsGroupDropzone } from './PhotoPrintsGroupDropzone';

const _PhotoPrintGroup = ({
    groupId, isEmpty,
    className, addPictures, onError
}: Props) => {

    const [isOpen, setIsOpen] = useState(false);

    const [dragDisabled, setDragDisabled] = useState(false);
    const [dragging, setDragging] = useState(false);

    const uploadMethods = usePhotoPrintsUploadMethods({
        externalUpload: { groupId },
        dropzone: { onChange: addPictures, onError }
    });

    const openUploadMethods = () => setIsOpen(true);

    return (
        <PhotoPrintsGroupDropzone
            className={className}
            groupId={groupId}
            disabled={dragDisabled}
            onDragStart={() => setDragging(true)}
            onDragEnd={() => setDragging(false)}>
            <>
                <PhotoPrintGroupPanel
                    dragging={dragging}
                    browse={openUploadMethods} groupId={groupId}>
                    {isEmpty ? (
                        <EmptyPhotoPrintsGroup openUploadMethods={openUploadMethods} />
                    ) : (
                        <NotEmptyPhotoPrintsGroup groupId={groupId} setDragDisabled={setDragDisabled} />
                    )}
                </PhotoPrintGroupPanel>
                <UploadMethodsWindow
                    methods={uploadMethods}
                    isOpen={isOpen}
                    close={() => setIsOpen(false)} />
            </>
        </PhotoPrintsGroupDropzone>
    );
};

type OwnProps = {
    groupId: string;
    className?: string;
};

type Props = OwnProps & {
    isEmpty: boolean;
    photos: PhotoPrintsJson.Photo[];
    photoIds: string[];
    addPictures: (files: File[]) => void;
    successfulUploadsCount: number;
    failedUploadsCount: number;
    onError: (errors: UploadError[]) => void;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    isEmpty: selectIsEmptyGroup(state, ownProps.groupId),
    photos: selectPhotosByGroupId(state, ownProps.groupId),
    photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId),
    successfulUploadsCount: selectSuccessfulUploadsCount(state, ownProps.groupId),
    failedUploadsCount: selectFailedUploadsCount(state, ownProps.groupId)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    addPictures: (files: File[]) => {
        dispatch(uploadImages(files, ownProps.groupId));
    },
    onError: (errors: UploadError[]) => {
        dispatch(incrementWithScheduledReset(ownProps.groupId, 'failedUploadsCount', errors.length));
        dispatch(updateWithScheduledReset({ id: ownProps.groupId, key: 'failedUploadsError', value: errors }));
    }
});

export const PhotoPrintsGroup = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintGroup);